import { useTranslation } from '../../i18n/useTranslation';
import { Dialog } from '../ui/Dialog/Dialog';
import { Button } from '../atoms/Button/Button';
import { Alert } from '../atoms/Alert/Alert';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import type { HistoryEntry } from '../../services/measurementService';
import { formatMeasuredAt } from '../../utils/measurementDate';
import { formatNumber, parameterLabel } from './measurementHistoryUtils';

export interface DeleteMeasurementDialogProps {
  entry: HistoryEntry | null;
  isDeleting: boolean;
  deleteFailed: boolean;
  onConfirm: (entry: HistoryEntry) => void;
  onClose: () => void;
}

export function DeleteMeasurementDialog({ entry, isDeleting, deleteFailed, onConfirm, onClose }: DeleteMeasurementDialogProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  if (!entry) return null;

  const label = parameterLabel(t, entry.parameterId, entry.parameterName);

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={t('measurement.history.delete.title')}
      size="sm"
      data-testid="delete-measurement-dialog"
      footer={
        <Inline gap="2" justify="end" wrap>
          <Button variant="secondary" onClick={onClose} disabled={isDeleting}>
            {t('measurement.history.delete.cancel')}
          </Button>
          <Button variant="danger" onClick={() => onConfirm(entry)} isLoading={isDeleting}>
            {t('measurement.history.delete.confirm')}
          </Button>
        </Inline>
      }
    >
      <Stack gap="4">
        {deleteFailed && <Alert tone="danger">{t('measurement.history.delete.failed')}</Alert>}
        <Text>
          {t('measurement.history.delete.question', {
            parameter: label,
            value: `${formatNumber(entry.value, language)} ${entry.unit}`.trim(),
            date: formatMeasuredAt(entry, language),
          })}
        </Text>
        {entry.hasEvidencePhoto && (
          <Text size="sm" color="secondary">{t('measurement.history.delete.photoNote')}</Text>
        )}
      </Stack>
    </Dialog>
  );
}

export default DeleteMeasurementDialog;
